/**
 * CouponTrackingPage — operator workspace (feature #171).
 *
 * Composition:
 *   - emerald->sky gradient header.
 *   - Campaign selector + mode filter chips.
 *   - Create-coupon form (mode-aware: exclusive/unique require publisher_id).
 *   - Bulk-generate form (unique mode only; count + prefix + length).
 *   - Coupons table with mode badge chips, usage cells, copy-to-clipboard
 *     on the code column, and per-row actions (toggle active, delete).
 *
 * Brand: emerald->sky gradient headline, zinc borders, 6-8px rounded, light
 * theme only. No emojis, no competitor names.
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import {
	COUPON_MODES,
	couponTrackingQueryKeys,
	createCoupon,
	deleteCoupon,
	formatUnixSec,
	formatUsage,
	generateBatch,
	listCoupons,
	modeBadgeClass,
	normalizeCode,
	statusOf,
	updateCoupon,
	validateGenerateCount,
	validateId,
	validateMode,
	validateUsageCap,
	type Coupon,
	type CouponMode,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 disabled:bg-zinc-50 disabled:text-zinc-500";
const SELECT = INPUT;
const BTN_PRIMARY =
	"rounded-[6px] border border-emerald-600 bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_NEUTRAL =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DANGER =
	"rounded-[6px] border border-rose-300 bg-white px-3 py-1.5 text-sm font-medium text-rose-600 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60";
const HINT = "text-xs text-zinc-500";
const CHIP_ACTIVE =
	"inline-flex items-center justify-center rounded-[6px] border border-emerald-600 bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white";
const CHIP_IDLE =
	"inline-flex items-center justify-center rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50";

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export interface CouponTrackingPageProps {
	initialMode?: CouponMode;
	initialCouponId?: string;
	initialCampaignId?: string;
}

export function CouponTrackingPage(props: CouponTrackingPageProps = {}) {
	const queryClient = useQueryClient();

	const [campaignId, setCampaignId] = useState<string>(
		props.initialCampaignId ?? "",
	);
	const [modeFilter, setModeFilter] = useState<"all" | CouponMode>(
		props.initialMode ?? "all",
	);

	const appliedCampaign = useMemo(
		() => validateId(campaignId) ?? null,
		[campaignId],
	);

	const couponsQuery = useQuery({
		enabled: appliedCampaign !== null,
		queryKey: appliedCampaign
			? couponTrackingQueryKeys.listByCampaign(appliedCampaign)
			: couponTrackingQueryKeys.all,
		queryFn: () => listCoupons(appliedCampaign!),
	});

	const createMutation = useMutation({
		mutationFn: createCoupon,
		onSuccess: (coupon) => {
			toast.success(`Coupon "${coupon.code}" created.`);
			queryClient.invalidateQueries({
				queryKey: couponTrackingQueryKeys.all,
			});
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to create coupon.");
		},
	});

	const updateMutation = useMutation({
		mutationFn: updateCoupon,
		onSuccess: () => {
			toast.success("Coupon updated.");
			queryClient.invalidateQueries({
				queryKey: couponTrackingQueryKeys.all,
			});
		},
		onError: (err: Error) => toast.error(err.message || "Update failed."),
	});

	const deleteMutation = useMutation({
		mutationFn: deleteCoupon,
		onSuccess: () => {
			toast.success("Coupon removed.");
			queryClient.invalidateQueries({
				queryKey: couponTrackingQueryKeys.all,
			});
		},
		onError: (err: Error) => toast.error(err.message || "Delete failed."),
	});

	const generateMutation = useMutation({
		mutationFn: generateBatch,
		onSuccess: (res) => {
			toast.success(`Generated ${res.count} unique codes.`);
			queryClient.invalidateQueries({
				queryKey: couponTrackingQueryKeys.all,
			});
		},
		onError: (err: Error) => toast.error(err.message || "Generation failed."),
	});

	const rows: readonly Coupon[] = useMemo(() => {
		const all = couponsQuery.data ?? [];
		if (modeFilter === "all") return all;
		return all.filter((c) => c.mode === modeFilter);
	}, [couponsQuery.data, modeFilter]);

	return (
		<main
			data-testid="coupon-tracking-page"
			className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 py-10 sm:py-14"
		>
			<Header />

			<CampaignSelector
				value={campaignId}
				onChange={setCampaignId}
				helpText={
					appliedCampaign
						? `Viewing campaign ${appliedCampaign}.`
						: "Enter a campaign id to load its coupons."
				}
			/>

			{appliedCampaign ? (
				<>
					<CreateCouponCard
						campaignId={appliedCampaign}
						onCreate={(args) => createMutation.mutate(args)}
						busy={createMutation.isPending}
					/>

					<GenerateBatchCard
						campaignId={appliedCampaign}
						onGenerate={(args) => generateMutation.mutate(args)}
						busy={generateMutation.isPending}
					/>

					<CouponsCard
						rows={rows}
						loading={couponsQuery.isLoading}
						error={couponsQuery.error?.message ?? null}
						modeFilter={modeFilter}
						onModeFilterChange={setModeFilter}
						onToggleActive={(c) =>
							updateMutation.mutate({ id: c.id, active: c.active !== 1 })
						}
						onDelete={(c) => {
							if (typeof window !== "undefined") {
								const ok = window.confirm(
									`Remove coupon "${c.code}"? Attributions stay, but the code is soft-deleted.`,
								);
								if (!ok) return;
							}
							deleteMutation.mutate(c.id);
						}}
						busy={updateMutation.isPending || deleteMutation.isPending}
					/>
				</>
			) : null}
		</main>
	);
}

export default CouponTrackingPage;

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Header() {
	return (
		<header className="flex flex-col gap-2">
			<p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
				Tracking
			</p>
			<h1 className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-3xl font-semibold tracking-tight text-transparent sm:text-4xl">
				Coupon codes
			</h1>
			<p className="max-w-prose text-sm text-zinc-600">
				Attribute offline and viral clicks via coupon codes. General codes live
				in a shared pool; exclusive codes reserve one publisher; unique codes
				burn on first use.
			</p>
		</header>
	);
}

function CampaignSelector(props: {
	value: string;
	onChange: (v: string) => void;
	helpText: string;
}) {
	return (
		<section className={`${CARD} p-5`} aria-label="Campaign selector">
			<h2 className="text-base font-semibold text-zinc-900">Campaign</h2>
			<p className={HINT}>{props.helpText}</p>
			<div className="mt-3 flex flex-col gap-1.5">
				<label htmlFor="campaign-id" className={LABEL}>
					Campaign id
				</label>
				<input
					id="campaign-id"
					name="campaign_id"
					type="text"
					className={`${INPUT} sm:max-w-md`}
					value={props.value}
					onChange={(e) => props.onChange(e.currentTarget.value)}
					placeholder="cmp_..."
					data-testid="campaign-id-input"
					maxLength={128}
				/>
			</div>
		</section>
	);
}

function CreateCouponCard(props: {
	campaignId: string;
	onCreate: (args: {
		campaign_id: string;
		mode: CouponMode;
		code: string;
		publisher_id?: string;
		usage_cap?: number | null;
	}) => void;
	busy: boolean;
}) {
	const [mode, setMode] = useState<CouponMode>("general");
	const [code, setCode] = useState("");
	const [publisherId, setPublisherId] = useState("");
	const [usageCap, setUsageCap] = useState<string>("");
	const [errors, setErrors] = useState<{
		code?: string;
		publisher?: string;
		cap?: string;
	}>({});

	const disabled = props.busy;

	const handleSubmit = (e: FormEvent) => {
		e.preventDefault();
		const next: typeof errors = {};

		const codeOk = normalizeCode(code);
		if (!codeOk) {
			next.code = "3-64 chars (letters, digits, . _ -). Uppercased on save.";
		}

		let pubOk: string | null = null;
		if (mode !== "general") {
			pubOk = validateId(publisherId);
			if (!pubOk) next.publisher = "Required for exclusive and unique modes.";
		} else if (publisherId.trim().length > 0) {
			next.publisher = "Forbidden in general mode — leave empty.";
		}

		const capOk = validateUsageCap(usageCap);
		if (capOk === "invalid") next.cap = "Enter a positive integer or leave blank.";

		setErrors(next);
		if (Object.keys(next).length > 0) return;

		props.onCreate({
			campaign_id: props.campaignId,
			mode,
			code: codeOk!,
			publisher_id: mode === "general" ? undefined : pubOk!,
			usage_cap:
				capOk === null || capOk === "invalid" ? undefined : capOk,
		});

		setCode("");
	};

	return (
		<section className={`${CARD} p-5`} aria-label="Create coupon">
			<h2 className="text-base font-semibold text-zinc-900">Create coupon</h2>
			<p className={HINT}>
				Exclusive and unique modes require a publisher_id. General mode resolves
				the publisher at attribution time.
			</p>

			<form
				onSubmit={handleSubmit}
				className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2"
				data-testid="create-coupon-form"
			>
				<div className="flex flex-col gap-1.5">
					<label htmlFor="mode-select" className={LABEL}>
						Mode
					</label>
					<select
						id="mode-select"
						name="mode"
						className={SELECT}
						value={mode}
						onChange={(e) => {
							const next = validateMode(e.currentTarget.value);
							if (next) setMode(next);
						}}
						disabled={disabled}
						data-testid="create-mode-select"
					>
						{COUPON_MODES.map((m) => (
							<option key={m} value={m}>
								{m}
							</option>
						))}
					</select>
				</div>

				<div className="flex flex-col gap-1.5">
					<label htmlFor="code-input" className={LABEL}>
						Code
					</label>
					<input
						id="code-input"
						name="code"
						type="text"
						required
						maxLength={64}
						className={INPUT}
						value={code}
						onChange={(e) => setCode(e.currentTarget.value)}
						disabled={disabled}
						data-testid="create-code-input"
					/>
					{errors.code ? (
						<p className="text-xs text-rose-600" role="alert">
							{errors.code}
						</p>
					) : null}
				</div>

				<div className="flex flex-col gap-1.5">
					<label htmlFor="publisher-input" className={LABEL}>
						Publisher id{" "}
						<span className="font-normal text-zinc-500">
							{mode === "general" ? "(leave blank)" : "(required)"}
						</span>
					</label>
					<input
						id="publisher-input"
						name="publisher_id"
						type="text"
						maxLength={128}
						className={INPUT}
						value={publisherId}
						onChange={(e) => setPublisherId(e.currentTarget.value)}
						disabled={disabled || mode === "general"}
						data-testid="create-publisher-input"
					/>
					{errors.publisher ? (
						<p className="text-xs text-rose-600" role="alert">
							{errors.publisher}
						</p>
					) : null}
				</div>

				<div className="flex flex-col gap-1.5">
					<label htmlFor="cap-input" className={LABEL}>
						Usage cap{" "}
						<span className="font-normal text-zinc-500">
							(optional — blank = unlimited)
						</span>
					</label>
					<input
						id="cap-input"
						name="usage_cap"
						type="number"
						min={1}
						className={INPUT}
						value={usageCap}
						onChange={(e) => setUsageCap(e.currentTarget.value)}
						disabled={disabled}
						data-testid="create-cap-input"
					/>
					{errors.cap ? (
						<p className="text-xs text-rose-600" role="alert">
							{errors.cap}
						</p>
					) : null}
				</div>

				<div className="sm:col-span-2">
					<button
						type="submit"
						className={BTN_PRIMARY}
						disabled={disabled}
						data-testid="create-coupon-submit"
					>
						{props.busy ? "Creating." : "Create coupon"}
					</button>
				</div>
			</form>
		</section>
	);
}

function GenerateBatchCard(props: {
	campaignId: string;
	onGenerate: (args: {
		campaign_id: string;
		publisher_id: string;
		count: number;
		usage_cap?: number | null;
		prefix?: string;
		length?: number;
	}) => void;
	busy: boolean;
}) {
	const [publisherId, setPublisherId] = useState("");
	const [count, setCount] = useState<string>("10");
	const [prefix, setPrefix] = useState<string>("");
	const [length, setLength] = useState<string>("10");
	const [errors, setErrors] = useState<{
		publisher?: string;
		count?: string;
		length?: string;
	}>({});

	const disabled = props.busy;

	const handleSubmit = (e: FormEvent) => {
		e.preventDefault();
		const next: typeof errors = {};

		const pubOk = validateId(publisherId);
		if (!pubOk) next.publisher = "Required for bulk-generate.";

		const countOk = validateGenerateCount(count);
		if (countOk === "invalid") next.count = "Enter an integer 1..500.";

		let lengthOk: number | undefined;
		if (length.trim().length > 0) {
			const n = Number(length);
			if (!Number.isFinite(n) || !Number.isInteger(n) || n < 4 || n > 24) {
				next.length = "4..24";
			} else {
				lengthOk = n;
			}
		}

		setErrors(next);
		if (Object.keys(next).length > 0) return;

		props.onGenerate({
			campaign_id: props.campaignId,
			publisher_id: pubOk!,
			count: countOk as number,
			prefix: prefix.length > 0 ? prefix : undefined,
			length: lengthOk,
		});
	};

	return (
		<section className={`${CARD} p-5`} aria-label="Bulk generate unique codes">
			<h2 className="text-base font-semibold text-zinc-900">
				Bulk-generate unique codes
			</h2>
			<p className={HINT}>
				Mints `count` unique-mode coupons for a publisher. Codes are uppercase
				alnum, skip visually ambiguous characters, and honour an optional
				prefix (e.g. "SUMMER-").
			</p>

			<form
				onSubmit={handleSubmit}
				className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-4"
				data-testid="generate-batch-form"
			>
				<div className="flex flex-col gap-1.5 sm:col-span-2">
					<label htmlFor="gen-publisher" className={LABEL}>
						Publisher id
					</label>
					<input
						id="gen-publisher"
						name="publisher_id"
						type="text"
						className={INPUT}
						value={publisherId}
						onChange={(e) => setPublisherId(e.currentTarget.value)}
						disabled={disabled}
						data-testid="generate-publisher-input"
					/>
					{errors.publisher ? (
						<p className="text-xs text-rose-600" role="alert">
							{errors.publisher}
						</p>
					) : null}
				</div>

				<div className="flex flex-col gap-1.5">
					<label htmlFor="gen-count" className={LABEL}>
						Count
					</label>
					<input
						id="gen-count"
						name="count"
						type="number"
						min={1}
						max={500}
						className={INPUT}
						value={count}
						onChange={(e) => setCount(e.currentTarget.value)}
						disabled={disabled}
						data-testid="generate-count-input"
					/>
					{errors.count ? (
						<p className="text-xs text-rose-600" role="alert">
							{errors.count}
						</p>
					) : null}
				</div>

				<div className="flex flex-col gap-1.5">
					<label htmlFor="gen-length" className={LABEL}>
						Length
					</label>
					<input
						id="gen-length"
						name="length"
						type="number"
						min={4}
						max={24}
						className={INPUT}
						value={length}
						onChange={(e) => setLength(e.currentTarget.value)}
						disabled={disabled}
						data-testid="generate-length-input"
					/>
					{errors.length ? (
						<p className="text-xs text-rose-600" role="alert">
							{errors.length}
						</p>
					) : null}
				</div>

				<div className="flex flex-col gap-1.5 sm:col-span-3">
					<label htmlFor="gen-prefix" className={LABEL}>
						Prefix (optional)
					</label>
					<input
						id="gen-prefix"
						name="prefix"
						type="text"
						maxLength={8}
						className={INPUT}
						value={prefix}
						onChange={(e) => setPrefix(e.currentTarget.value)}
						disabled={disabled}
						data-testid="generate-prefix-input"
					/>
				</div>

				<div className="flex items-end">
					<button
						type="submit"
						className={BTN_PRIMARY}
						disabled={disabled}
						data-testid="generate-batch-submit"
					>
						{props.busy ? "Generating." : "Generate"}
					</button>
				</div>
			</form>
		</section>
	);
}

function CouponsCard(props: {
	rows: readonly Coupon[];
	loading: boolean;
	error: string | null;
	modeFilter: "all" | CouponMode;
	onModeFilterChange: (v: "all" | CouponMode) => void;
	onToggleActive: (coupon: Coupon) => void;
	onDelete: (coupon: Coupon) => void;
	busy: boolean;
}) {
	return (
		<section className={`${CARD} flex flex-col`} aria-label="Coupons">
			<header className="flex flex-col gap-3 border-b border-zinc-200 p-5 sm:flex-row sm:items-end sm:justify-between">
				<div className="flex flex-col gap-1">
					<h2 className="text-base font-semibold text-zinc-900">
						Coupons ({props.rows.length})
					</h2>
					<p className={HINT}>
						Click a code to copy it. Exclusive and unique modes honor the
						publisher on the coupon row; general mode resolves at attribute
						time.
					</p>
				</div>

				<div
					className="flex flex-wrap items-center gap-2"
					role="tablist"
					aria-label="Filter by mode"
				>
					<FilterChip
						active={props.modeFilter === "all"}
						onClick={() => props.onModeFilterChange("all")}
						label="All"
					/>
					{COUPON_MODES.map((m) => (
						<FilterChip
							key={m}
							active={props.modeFilter === m}
							onClick={() => props.onModeFilterChange(m)}
							label={m}
						/>
					))}
				</div>
			</header>

			{props.error ? (
				<div className="p-5 text-sm text-rose-600" role="alert">
					{props.error}
				</div>
			) : props.loading ? (
				<div className="p-5 text-sm text-zinc-500">Loading coupons.</div>
			) : props.rows.length === 0 ? (
				<div className="flex flex-col items-center gap-1 p-10 text-center">
					<p className="text-sm font-medium text-zinc-700">
						No coupons match the current filter.
					</p>
					<p className="text-xs text-zinc-500">
						Create a coupon or bulk-generate unique codes to get started.
					</p>
				</div>
			) : (
				<CouponsTable
					rows={props.rows}
					onToggleActive={props.onToggleActive}
					onDelete={props.onDelete}
					busy={props.busy}
				/>
			)}
		</section>
	);
}

function FilterChip(props: {
	active: boolean;
	onClick: () => void;
	label: string;
}) {
	return (
		<button
			type="button"
			onClick={props.onClick}
			className={props.active ? CHIP_ACTIVE : CHIP_IDLE}
			aria-pressed={props.active}
		>
			{props.label}
		</button>
	);
}

function CouponsTable(props: {
	rows: readonly Coupon[];
	onToggleActive: (coupon: Coupon) => void;
	onDelete: (coupon: Coupon) => void;
	busy: boolean;
}) {
	return (
		<div className="overflow-x-auto">
			<table
				className="w-full border-collapse text-left text-sm"
				data-testid="coupons-table"
			>
				<thead className="bg-zinc-50 text-xs font-medium uppercase tracking-wide text-zinc-500">
					<tr>
						<Th>Code</Th>
						<Th>Mode</Th>
						<Th>Publisher</Th>
						<Th>Usage</Th>
						<Th>Status</Th>
						<Th>Created</Th>
						<Th className="text-right">Actions</Th>
					</tr>
				</thead>
				<tbody className="divide-y divide-zinc-100">
					{props.rows.map((c) => (
						<CouponsRow
							key={c.id}
							coupon={c}
							onToggleActive={props.onToggleActive}
							onDelete={props.onDelete}
							busy={props.busy}
						/>
					))}
				</tbody>
			</table>
		</div>
	);
}

function CouponsRow(props: {
	coupon: Coupon;
	onToggleActive: (coupon: Coupon) => void;
	onDelete: (coupon: Coupon) => void;
	busy: boolean;
}) {
	const { coupon } = props;
	const status = statusOf(coupon);

	const handleCopy = () => {
		const nav =
			typeof navigator !== "undefined"
				? (navigator as Navigator & {
						clipboard?: { writeText: (v: string) => Promise<void> };
				  })
				: null;
		const clipboard = nav?.clipboard;
		if (!clipboard) {
			toast.error("Clipboard unavailable in this browser.");
			return;
		}
		clipboard
			.writeText(coupon.code)
			.then(() => toast.success(`Copied "${coupon.code}" to clipboard.`))
			.catch(() => toast.error("Copy failed."));
	};

	return (
		<tr data-testid={`coupons-row-${coupon.id}`}>
			<Td>
				<button
					type="button"
					onClick={handleCopy}
					className="inline-flex items-center gap-1 rounded-[6px] px-1.5 py-0.5 font-mono text-sm text-zinc-900 hover:bg-zinc-100"
					title="Click to copy"
					data-testid={`copy-code-${coupon.id}`}
				>
					{coupon.code}
				</button>
			</Td>
			<Td>
				<span className={modeBadgeClass(coupon.mode)}>{coupon.mode}</span>
			</Td>
			<Td>
				{coupon.publisher_id ? (
					<span className="font-mono text-xs text-zinc-700">
						{coupon.publisher_id}
					</span>
				) : (
					<span className="text-xs text-zinc-500">(pool)</span>
				)}
			</Td>
			<Td>
				<span className="font-mono text-xs text-zinc-700">
					{formatUsage(coupon)}
				</span>
			</Td>
			<Td>
				<StatusPill status={status} />
			</Td>
			<Td>
				<span className="text-xs text-zinc-600">
					{formatUnixSec(coupon.created_at)}
				</span>
			</Td>
			<Td className="text-right">
				<div className="inline-flex items-center gap-2">
					<button
						type="button"
						className={BTN_NEUTRAL}
						onClick={() => props.onToggleActive(coupon)}
						disabled={props.busy}
						data-testid={`toggle-active-${coupon.id}`}
					>
						{coupon.active === 1 ? "Disable" : "Enable"}
					</button>
					<button
						type="button"
						className={BTN_DANGER}
						onClick={() => props.onDelete(coupon)}
						disabled={props.busy}
						data-testid={`delete-${coupon.id}`}
					>
						Delete
					</button>
				</div>
			</Td>
		</tr>
	);
}

function StatusPill(props: { status: ReturnType<typeof statusOf> }) {
	const base =
		"inline-flex items-center gap-1 rounded-[6px] px-2 py-0.5 text-xs font-medium";
	switch (props.status) {
		case "active":
			return (
				<span
					className={`${base} border border-emerald-200 bg-emerald-50 text-emerald-700`}
				>
					Active
				</span>
			);
		case "inactive":
			return (
				<span
					className={`${base} border border-zinc-200 bg-zinc-50 text-zinc-700`}
				>
					Inactive
				</span>
			);
		case "exhausted":
			return (
				<span
					className={`${base} border border-amber-200 bg-amber-50 text-amber-700`}
				>
					Exhausted
				</span>
			);
		case "deleted":
			return (
				<span
					className={`${base} border border-rose-200 bg-rose-50 text-rose-700`}
				>
					Deleted
				</span>
			);
	}
}

function Th(props: {
	children: React.ReactNode;
	className?: string;
}) {
	return (
		<th className={`px-4 py-3 font-medium ${props.className ?? ""}`}>
			{props.children}
		</th>
	);
}

function Td(props: {
	children: React.ReactNode;
	className?: string;
}) {
	return (
		<td className={`px-4 py-3 align-middle ${props.className ?? ""}`}>
			{props.children}
		</td>
	);
}
